import React from "react";
import QRCodeRenderer from "../shared/QRCodeRenderer";
import Button from "../shared/Button";
import {
  getMealPreferenceLabel,
  getStatusLabel,
  getStatusColor,
} from "../../types/registration.types";
import { getCheckInQRUrl } from "../../hooks/useRegistration";
import "./RegistrationConfirmation.css";
import { EventSummaryDTO } from "../../types/event.types";
import { EventRegistration, RegistrationStatus } from "../../types/shared.types";

interface RegistrationConfirmationProps {
  registration: EventRegistration;
  event: EventSummaryDTO;
  onRegisterAnother?: () => void;
}

const RegistrationConfirmation: React.FC<RegistrationConfirmationProps> = ({
  registration,
  event,
}) => {
  const isAccepted = registration.status === RegistrationStatus.Registered;
  const isTentative = registration.status === RegistrationStatus.Tentative;
  const isDeclined = registration.status === RegistrationStatus.Declined;

  const checkInUrl = registration.checkInCode
    ? getCheckInQRUrl(registration.checkInCode)
    : "";

  const formatEventDate = (isoDate: string): string =>
    new Date(isoDate).toLocaleDateString("de-DE", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const handleDownloadQR = () => {
    const qrElement = document.getElementById("confirmation-qr-code");
    if (!qrElement) return;

    const svgElement = qrElement.querySelector("svg");
    if (!svgElement) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      ctx.scale(2, 2);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const link = document.createElement("a");
      link.download = `${event.eventName.replace(/[^a-z0-9]/gi, "_")}_checkin_qr.png`;
      link.href = canvas.toDataURL("image/png");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    img.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <div className="registration-page">
      <div className="confirmation-container">
        <div
          className={`confirmation-icon ${registration.status.toLowerCase()}`}
        >
          {isAccepted && "✓"}
          {isTentative && "?"}
          {isDeclined && "✗"}
        </div>

        <h1 className="confirmation-title">
          {isAccepted && "Registration Confirmed!"}
          {isTentative && "Tentative Registration Recorded"}
          {isDeclined && "Response Recorded"}
        </h1>

        <p className="confirmation-subtitle">
          {isAccepted &&
            "You're all set! Show the QR code below at the event entrance."}
          {isTentative &&
            "We've noted your tentative response. You can update it anytime."}
          {isDeclined &&
            "Thank you for letting us know. We hope to see you at future events."}
        </p>

        <div className="confirmation-details">
          <div className="detail-item">
            <span className="detail-label">Event</span>
            <span className="detail-value">{event.eventName}</span>
          </div>

          <div className="detail-item">
            <span className="detail-label">Date</span>
            <span className="detail-value">
              {formatEventDate(event.eventStartDate)} –{" "}
              {formatEventDate(event.eventEndDate)}
            </span>
          </div>

          <div className="detail-item">
            <span className="detail-label">Location</span>
            <span className="detail-value">{event.location}</span>
          </div>

          <div className="detail-item">
            <span className="detail-label">Name</span>
            <span className="detail-value">{registration.userName}</span>
          </div>

          <div className="detail-item">
            <span className="detail-label">Status</span>
            <span
              className="detail-value status-badge"
              style={{ backgroundColor: getStatusColor(registration.status) }}
            >
              {getStatusLabel(registration.status)}
            </span>
          </div>

          {registration.mealPreference &&
            registration.mealPreference !== "None" && (
              <div className="detail-item">
                <span className="detail-label">Meal Preference</span>
                <span className="detail-value">
                  {getMealPreferenceLabel(registration.mealPreference)}
                </span>
              </div>
            )}
        </div>

        {isAccepted && registration.checkInCode && (
          <div className="qr-section">
            <h2 className="qr-title">Your Check-in QR Code</h2>
            <p className="qr-subtitle">
              Screenshot or download this QR code and show it at the event
              entrance
            </p>

            <div className="qr-code-wrapper" id="confirmation-qr-code">
              <QRCodeRenderer
                value={checkInUrl}
                size={200}
                withLogo={true}
                logoUrl="/images/CGI-Logo.jpg"
              />
            </div>

            <div className="check-in-code">
              <span className="code-label">Check-in Code:</span>
              <span className="code-value">{registration.checkInCode}</span>
            </div>

            <div className="qr-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={handleDownloadQR}
              >
                Download QR Code
              </Button>
            </div>
          </div>
        )}

        <div className="confirmation-footer">
          {isAccepted && (
            <p>
              Need to change your response? Visit{" "}
              <a href="/my-registrations">My Registrations</a> to update your
              registration.
            </p>
          )}
          {isTentative && (
            <p>
              Please confirm your attendance before the deadline. Visit{" "}
              <a href="/my-registrations">My Registrations</a> to update your
              response.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationConfirmation;
